import axios from 'axios';
import { getAccessToken, getRefreshToken, saveTokens, clearTokens } from '../utils/tokenHelper';

const isLoopbackHost = ['localhost', '127.0.0.1', '[::1]'].includes(
  window.location.hostname
);
const LOCAL_BASE_URL =
  process.env.REACT_APP_LOCAL_API_BASE_URL ||
  (process.env.NODE_ENV === 'development'
    ? process.env.REACT_APP_API_BASE_URL
    : null) ||
  'https://localhost:7287/api';
const HOSTED_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://koplpune.in/kopld/api';
const BASE_URL = isLoopbackHost ? LOCAL_BASE_URL : HOSTED_BASE_URL;

const API_SCOPE_KEY = 'dp_api_base_url';
if (isLoopbackHost) {
  const previousBaseUrl = localStorage.getItem(API_SCOPE_KEY);
  if (previousBaseUrl !== BASE_URL && getAccessToken()) clearTokens();
  localStorage.setItem(API_SCOPE_KEY, BASE_URL);
}

const api = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) config.headers['Authorization'] = `Bearer ${token}`;

    // Let the browser add multipart/form-data with its required boundary.
    // Keeping the JSON default on FormData causes ASP.NET Core HTTP 415.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (typeof config.headers?.delete === 'function') {
        config.headers.delete('Content-Type');
      } else if (config.headers) {
        delete config.headers['Content-Type'];
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(p => error ? p.reject(error) : p.resolve(token));
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    if (error.response?.config?.responseType === 'blob' && error.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        error.response.data = JSON.parse(text);
      } catch { /* leave as blob */ }
    }

    if (
      error.response?.status === 401 &&
      !original._retry &&
      !original.url?.includes('/auth/')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
        .then(token => {
          original.headers['Authorization'] = `Bearer ${token}`;
          return api(original);
        })
        .catch(err => Promise.reject(err));
      }

      original._retry = true;
      isRefreshing = true;
      const refreshTk = getRefreshToken();
      if (!refreshTk) {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const res = await axios.post(`${BASE_URL}/auth/refresh-token`, {
          refreshToken: refreshTk
        });

        if (res.data?.success) {
          const { accessToken, refreshToken } = res.data.data;
          saveTokens(accessToken, refreshToken);
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
          original.headers['Authorization'] = `Bearer ${accessToken}`;
          processQueue(null, accessToken);
          return api(original);
        }

        processQueue(error, null);
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      } catch (refreshError) {
        processQueue(refreshError, null);
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const registerSendOtp   = (data) => api.post('/auth/register/send-otp',        data);
export const registerVerifyOtp = (data) => api.post('/auth/register/verify-otp',      data);
export const registerComplete  = (data) => api.post('/auth/register/complete',         data);
export const login             = (data) => api.post('/auth/login',                     data);
export const forgotPwSendOtp   = (data) => api.post('/auth/forgot-password/send-otp', data);
export const forgotPwReset     = (data) => api.post('/auth/forgot-password/reset',    data);
export const refreshToken      = (data) => api.post('/auth/refresh-token',             data);
export const logout            = (data) => api.post('/auth/logout',                   data);

export default api;
